// State-level marketing content for the six New England state pages.
//
// Purchaser types and representative source names are drawn from the
// application's current active source configuration (config/sources.json,
// inspected read-only). No internal scraper identifiers are used.
// The browse URLs follow the application's verified public contract:
// https://app.procuralerts.com/bids?state=<CC> (state query parameter is
// read by the public browse page and its API).
//
// The official-channel sections (officialOpportunities, gettingStarted,
// registrationNotes, certificationNotes, prevailingWageNotes,
// officialResources) are drawn exclusively from primary government sources
// verified live on 2026-08-06; the exact source URL for every claim is
// recorded in
// research/release_readiness/NEW_ENGLAND_STATE_PAGE_AUTHORITY_REMEDIATION.md.
// Thresholds and program names can change — re-verify before editing, and
// keep STATE_CONTENT_REVIEW_DATE in step with the last verification.

import { SITE_URL } from './constants'

export interface StateOfficialResource {
  /** Stable per-state identifier used by direct-answer citations. */
  id: string
  label: string
  url: string
}

/** One concise, source-supported direct answer in the "At a glance" block.
 * Answers reuse only facts already verified in the official-source sections;
 * `resourceIds` must reference this state's own officialResources. */
export interface StateDirectAnswer {
  /** Stable identifier, e.g. 'where-to-find' (same across states). */
  id: string
  /** The natural contractor question this entry answers. */
  question: string
  /** Concise, state-specific answer (no shared generic boilerplate). */
  answer: string
  /** IDs of this state's officialResources supporting the answer. */
  resourceIds: string[]
}

export interface StateContent {
  slug: string
  name: string
  abbreviation: string
  title: string
  description: string
  heroCopy: string
  purchaserTypes: string[]
  representativeSources: string[]
  opportunityCategories: string[]
  stateValueCopy: string
  browseUrl: string
  browseLabel: string
  /** Where the state's official opportunities are posted (paragraphs). */
  officialOpportunities: string[]
  /** Practical first steps for a prospective vendor, in order. */
  gettingStarted: string[]
  /** Vendor registration vs. licensing vs. prequalification (paragraphs). */
  registrationNotes: string[]
  /** Official supplier-diversity / certification programs (paragraphs). */
  certificationNotes: string[]
  /** Plain-language prevailing-wage overview (paragraphs). */
  prevailingWageNotes: string[]
  /** Official government links, verified live on the review date. */
  officialResources: StateOfficialResource[]
  /** Concise source-supported answers rendered in the "At a glance" block. */
  directAnswers: StateDirectAnswer[]
  /** Task-focused guide links surfaced contextually in the matching sections
   * (registration, certification, prevailing wage). */
  taskGuides?: { slug: string; label: string }[]
}

/** Date the official-source sections were last verified against the
 * primary government sources. */
export const STATE_CONTENT_REVIEW_DATE = 'August 6, 2026'

const APP_BIDS = 'https://app.procuralerts.com/bids'

export const STATES: StateContent[] = [
  {
    slug: 'massachusetts',
    name: 'Massachusetts',
    abbreviation: 'MA',
    title: 'Massachusetts Government Bids for Contractors | ProcurAlerts',
    description:
      'Track Massachusetts government bids from COMMBUYS, the Central Register, cities and towns, school districts, housing authorities, and transit agencies — matched to your trade and service area.',
    heroCopy:
      'Massachusetts posts public work through more channels than almost any state in the region — the COMMBUYS statewide portal, the Central Register, and hundreds of municipal, educational, and agency buyers. ProcurAlerts brings those postings together so Bay State contractors can see the opportunities that match your trade and territory without checking each channel by hand.',
    purchaserTypes: [
      'the statewide COMMBUYS procurement portal and the Massachusetts Central Register',
      'cities and towns across the Commonwealth',
      'public school districts and the Massachusetts School Building Authority',
      'housing authorities and MassHousing',
      'the MBTA and regional transit authorities',
    ],
    representativeSources: [
      'COMMBUYS — Commonwealth of Massachusetts',
      'Massachusetts Central Register',
      'City of Boston',
      'Massachusetts Bay Transportation Authority',
      'MassHousing',
    ],
    opportunityCategories: [
      'general construction and public building work',
      'electrical, HVAC, and plumbing projects',
      'road, sitework, and paving contracts',
      'school construction and renovation',
      'janitorial and facilities maintenance',
      'engineering and design services',
      'vehicles, fleet, and equipment',
    ],
    stateValueCopy:
      'Massachusetts contractors face a uniquely fragmented landscape: a state portal, a legal-notice register, 351 cities and towns, regional transit agencies, and hundreds of school and housing buyers, each posting on its own schedule. Staying on top of that mix manually is a part-time job. ProcurAlerts monitors the full range daily and matches postings to your trades and counties, so a resurfacing contract in Worcester County and a school renovation in Suffolk County land in the same morning digest.',
    browseUrl: `${APP_BIDS}?state=MA`,
    browseLabel: 'Browse Massachusetts bids',
    officialOpportunities: [
      'COMMBUYS is the Commonwealth’s official procurement platform, run by the Operational Services Division. Open state bids can be browsed publicly on commbuys.com without logging in, and a free vendor account adds email notifications and electronic response for state solicitations.',
      'Public construction is advertised through several official channels in addition to COMMBUYS: the Central Register, published weekly by the Secretary of the Commonwealth, carries design and building project notices; DCAMM posts state building solicitations; and MassDOT advertises highway lettings. COMMBUYS access is free; full weekly delivery of the Central Register requires a paid subscription to the Secretary of the Commonwealth’s publications service, although individual notices can be searched free online. Cities, towns, school districts, and authorities post on their own purchasing pages.',
    ],
    gettingStarted: [
      'Register a free vendor account in COMMBUYS and choose accurate UNSPSC commodity codes — those codes drive the state’s bid-notification emails.',
      'Review open bids on COMMBUYS, then check the construction channels that fit your work: the Central Register for public building notices, DCAMM for state building projects, and MassDOT for highway lettings.',
      'Before bidding public building work estimated at $150,000 or more under M.G.L. c. 149 or c. 149A, confirm your DCAMM contractor certification is current — it is a statutory bid prerequisite for those projects and is specific to your categories of work. Highway bidders should confirm MassDOT prequalification instead.',
      'Read the full solicitation, including the project-specific prevailing-wage rate sheet and any bonding, insurance, and certification requirements.',
      'Track the official posting for addenda through the deadline, and submit exactly as the awarding authority instructs.',
    ],
    registrationNotes: [
      'Vendor registration and construction certification are separate steps. A COMMBUYS vendor account creates your record with the Commonwealth and enables notifications and electronic responses — it does not certify your firm for construction work.',
      'DCAMM contractor certification is required when a Massachusetts public awarding authority bids a vertical (building) project estimated at $150,000 or more under M.G.L. c. 149 or c. 149A; certification is granted by category of work and renews annually. MassDOT runs its own prequalification program for highway construction bidders. Separately, individuals supervising certain building work generally need a Construction Supervisor License, and trades such as electrical and plumbing are licensed individually. Which of these applies depends on the project — confirm with the awarding authority.',
    ],
    certificationNotes: [
      'The state’s Supplier Diversity Office (SDO) certifies minority-, women-, veteran-, and Portuguese-owned business enterprises (MBE, WBE, VBE, PBE), recognizes certain third-party certifications, and runs the Small Business Purchasing Program for Massachusetts-based small businesses. Eligibility rules are program-specific, and whether a certification affects an award depends on the solicitation.',
      'For federally assisted transportation contracts, MassDOT administers the federal Disadvantaged Business Enterprise (DBE) program.',
    ],
    prevailingWageNotes: [
      'Massachusetts has its own prevailing-wage law (G.L. c. 149, §§ 26–27) covering public building and public works construction. The Department of Labor Standards issues project-specific rate sheets that awarding authorities obtain before soliciting bids, covered contractors submit certified weekly payrolls, and the Attorney General’s Fair Labor Division enforces the law.',
      'Which rate schedule and obligations apply depends on the project and its funding. Price labor from the wage determination incorporated into the solicitation, and confirm questions with the awarding authority — this overview is not legal advice.',
    ],
    officialResources: [
      { id: 'commbuys', label: 'COMMBUYS — the Commonwealth’s procurement platform', url: 'https://www.commbuys.com' },
      { id: 'commbuys-vendor-registration', label: 'COMMBUYS vendor registration guide', url: 'https://www.mass.gov/how-to/commbuys-vendor-registration' },
      { id: 'osd', label: 'Operational Services Division', url: 'https://www.mass.gov/orgs/operational-services-division' },
      { id: 'dcamm-certification', label: 'DCAMM contractor certification', url: 'https://www.mass.gov/dcamm-contractor-certification' },
      { id: 'ma-prevailing-wage', label: 'Prevailing wage guidance (Dept. of Labor Standards)', url: 'https://www.mass.gov/info-details/massachusetts-prevailing-wage-law-for-contractors' },
      { id: 'sdo', label: 'Supplier Diversity Office', url: 'https://www.mass.gov/orgs/supplier-diversity-office-sdo' },
    ],
    directAnswers: [
      {
        id: 'where-to-find',
        question: 'Where do contractors find official opportunities?',
        answer:
          'State solicitations are posted on COMMBUYS, where open bids can be browsed free without logging in. Public building notices also appear in the weekly Central Register, and DCAMM and MassDOT post construction solicitations through their own channels; cities, towns, schools, and authorities post separately.',
        resourceIds: ['commbuys', 'osd'],
      },
      {
        id: 'vendor-registration',
        question: 'Is vendor registration required?',
        answer:
          'Browsing COMMBUYS needs no account, but a free vendor account is what enables the state’s bid-notification emails and electronic responses to state solicitations — choose accurate UNSPSC commodity codes, because they drive the notifications.',
        resourceIds: ['commbuys-vendor-registration'],
      },
      {
        id: 'prequalification',
        question: 'Can prequalification or licensing apply?',
        answer:
          'For public building work estimated at $150,000 or more under M.G.L. c. 149 or c. 149A, current DCAMM contractor certification is a statutory bid prerequisite, granted by category of work. Highway bidders confirm MassDOT prequalification instead, and construction-supervisor and trade licenses are separate requirements that depend on the project.',
        resourceIds: ['dcamm-certification'],
      },
      {
        id: 'certifications',
        question: 'Which certifications deserve review?',
        answer:
          'The Supplier Diversity Office certifies MBE, WBE, VBE, and PBE firms and runs the Small Business Purchasing Program; MassDOT administers the federal DBE program for assisted transportation contracts. Whether a certification affects an award depends on the solicitation.',
        resourceIds: ['sdo'],
      },
      {
        id: 'prevailing-wage',
        question: 'What is the prevailing-wage rule?',
        answer:
          'Massachusetts has its own prevailing-wage law (G.L. c. 149, §§ 26–27) covering public building and public works construction, with project-specific rate sheets issued by the Department of Labor Standards. Price labor from the wage determination incorporated into the solicitation.',
        resourceIds: ['ma-prevailing-wage'],
      },
    ],
    taskGuides: [
      { slug: 'commbuys-vendor-registration', label: 'how to register as a COMMBUYS vendor' },
      { slug: 'dcamm-contractor-certification', label: 'DCAMM contractor certification' },
      { slug: 'new-england-prevailing-wage', label: 'prevailing wage across New England' },
    ],
  },
  {
    slug: 'connecticut',
    name: 'Connecticut',
    abbreviation: 'CT',
    title: 'Connecticut Government Bids for Contractors | ProcurAlerts',
    description:
      'Find Connecticut government bids from CTsource, municipalities like Stamford and Waterbury, the University of Connecticut, regional councils, and public agencies — matched to your trade.',
    heroCopy:
      'Connecticut contractors juggle the CTsource state portal alongside a dense map of city and town purchasing offices, regional councils, and public institutions. ProcurAlerts watches those sources every day and delivers the Connecticut opportunities that fit your trade and service area in one place.',
    purchaserTypes: [
      'the CTsource state procurement portal',
      'cities and towns, from Stamford and Norwalk to Waterbury and New Britain',
      'the University of Connecticut',
      'regional councils such as CRCOG',
      'public authorities including the Connecticut Port Authority',
      'regional education bodies such as the Capitol Region Education Council',
    ],
    representativeSources: [
      'CTsource — Connecticut State Procurement Portal',
      'City of Stamford',
      'City of Waterbury',
      'University of Connecticut',
      'Connecticut Port Authority',
    ],
    opportunityCategories: [
      'general construction and renovation',
      'roofing, masonry, and concrete work',
      'HVAC and mechanical upgrades',
      'electrical and low-voltage systems',
      'sitework, paving, and grounds',
      'painting and finishing',
      'IT and technology services',
    ],
    stateValueCopy:
      'In Connecticut, meaningful public work is split between a centralized state portal and 169 municipalities that each run their own purchasing process — plus universities, port and regional authorities with their own boards and calendars. A contractor based in Hartford or Fairfield County can easily miss a well-fitted posting two towns over. ProcurAlerts normalizes those channels into a single trade-matched feed so coverage does not depend on which portal you remembered to check.',
    browseUrl: `${APP_BIDS}?state=CT`,
    browseLabel: 'Browse Connecticut bids',
    officialOpportunities: [
      'CTsource is Connecticut’s official State Contracting Portal, managed by the Department of Administrative Services (DAS). Its public Bid Board lists open state solicitations for goods, services, and IT, and DAS notes that registration is not required just to view opportunities. CTDOT construction advertisements and project documents are also distributed through the portal.',
      'Outside the state portal, each of Connecticut’s 169 municipalities runs its own purchasing, and public universities, regional councils, and authorities post separately — which is why state-level and local tracking both matter here.',
    ],
    gettingStarted: [
      'Create a supplier account in CTsource — it is needed to respond electronically to state solicitations and to receive opportunity notifications.',
      'Browse the CTsource Bid Board for state solicitations in your commodity areas.',
      'For public building construction, check DAS contractor prequalification: DAS currently requires prequalification in designated classifications before bidding publicly funded building contracts estimated over $1,000,000, and may require additional qualification documentation with bids on contracts between $500,000 and $1,000,000. Highway and bridge bidders use CTDOT’s separate prequalification system.',
      'Review each solicitation for prevailing-wage, bonding, insurance, and submission requirements.',
      'Watch the official posting for addenda and submit through the method the issuing agency specifies.',
    ],
    registrationNotes: [
      'Registering in CTsource creates your vendor record with the state; it does not prequalify or license your firm. Construction prequalification is a separate DAS program for larger publicly funded building contracts, and CTDOT prequalifies highway and bridge contractors through its own application.',
      'Licensing is activity-specific: the Department of Consumer Protection registers home improvement and new home construction contractors and licenses trade contractors such as electrical, plumbing, and HVAC. Confirm which credentials a solicitation requires before pricing the work.',
    ],
    certificationNotes: [
      'DAS operates Connecticut’s Small Business Certification program, certifying Small Business Enterprises (SBE) and Minority Business Enterprises (MBE) — Connecticut’s MBE definition includes women-owned and disability-owned firms — and maintains the official certified-vendor directory. State agencies set annual spending goals for certified small and minority businesses.',
      'CTDOT separately administers Disadvantaged Business Enterprise (DBE) certification for federally assisted transportation contracts. Whether certification helps on a given opportunity depends on the program rules and the solicitation.',
    ],
    prevailingWageNotes: [
      'Connecticut has its own prevailing-wage law (CGS § 31-53), administered by the Department of Labor’s Wage and Workplace Standards Division. Under the statute, it applies to new construction projects with a total cost of $1,000,000 or more and to remodeling, refinishing, refurbishing, rehabilitation, alteration, or repair projects of $100,000 or more, with rates adjusted annually.',
      'Coverage depends on project cost, type, and funding, so confirm whether a solicitation carries prevailing-wage obligations with the issuing agency. This overview is not legal advice.',
    ],
    officialResources: [
      { id: 'ctsource-bidboard', label: 'CTsource Bid Board', url: 'https://portal.ct.gov/das/ctsource/bidboard' },
      { id: 'ctsource-portal', label: 'CTsource portal and supplier registration', url: 'https://portal.ct.gov/das/procurement-programs-and-services/ctsource' },
      { id: 'das-prequalification', label: 'DAS contractor prequalification', url: 'https://portal.ct.gov/das/procurement-programs-and-services/contractor--prequalification' },
      { id: 'ct-prevailing-wage', label: 'CT DOL prevailing wage', url: 'https://portal.ct.gov/dol/divisions/wage-and-workplace-standards/prevailing-wage' },
      { id: 'das-small-business', label: 'DAS Small Business Certification (SBE/MBE)', url: 'https://portal.ct.gov/das/procurement-programs-and-services/small-business' },
      { id: 'das-procurement', label: 'DAS Procurement Division', url: 'https://portal.ct.gov/das/procurement' },
    ],
    directAnswers: [
      {
        id: 'where-to-find',
        question: 'Where do contractors find official opportunities?',
        answer:
          'State solicitations are listed on the public CTsource Bid Board, and CTDOT construction advertisements are also distributed through the portal. Each of Connecticut’s 169 municipalities, the universities, and regional authorities post separately.',
        resourceIds: ['ctsource-bidboard', 'ctsource-portal'],
      },
      {
        id: 'vendor-registration',
        question: 'Is vendor registration required?',
        answer:
          'DAS notes that registration is not required just to view opportunities, but a CTsource supplier account is needed to respond electronically to state solicitations and to receive opportunity notifications.',
        resourceIds: ['ctsource-portal'],
      },
      {
        id: 'prequalification',
        question: 'Can prequalification or licensing apply?',
        answer:
          'DAS requires prequalification in designated classifications before bidding publicly funded building contracts estimated over $1,000,000, and may require additional qualification documentation with bids between $500,000 and $1,000,000. Highway and bridge bidders use CTDOT’s separate system, and trade licensing runs through the Department of Consumer Protection.',
        resourceIds: ['das-prequalification'],
      },
      {
        id: 'certifications',
        question: 'Which certifications deserve review?',
        answer:
          'DAS certifies Small Business Enterprises (SBE) and Minority Business Enterprises (MBE) — Connecticut’s MBE definition includes women- and disability-owned firms — and CTDOT administers federal DBE certification for assisted transportation contracts.',
        resourceIds: ['das-small-business'],
      },
      {
        id: 'prevailing-wage',
        question: 'What is the prevailing-wage rule?',
        answer:
          'Connecticut has its own prevailing-wage law (CGS § 31-53): it applies to new construction of $1,000,000 or more and to remodeling, refinishing, refurbishing, rehabilitation, alteration, or repair of $100,000 or more, administered by the Department of Labor. Confirm coverage and funding in each solicitation.',
        resourceIds: ['ct-prevailing-wage'],
      },
    ],
    taskGuides: [
      { slug: 'ctsource-contractor-registration', label: 'how to register and respond on CTsource' },
      { slug: 'new-england-prevailing-wage', label: 'prevailing wage across New England' },
    ],
  },
  {
    slug: 'maine',
    name: 'Maine',
    abbreviation: 'ME',
    title: 'Maine Government Bids for Contractors | ProcurAlerts',
    description:
      'Discover Maine government bids from the State of Maine VSS, MaineDOT, the Bureau of General Services, cities like Portland and Bangor, and regional public agencies — matched to your trade.',
    heroCopy:
      'Maine’s public work stretches from Portland and Lewiston to Bangor and beyond, posted by the state’s VSS portal, MaineDOT, municipal offices, and regional agencies. ProcurAlerts monitors those sources daily so Maine contractors see matched opportunities without crisscrossing the state’s many posting sites.',
    purchaserTypes: [
      'the State of Maine VSS vendor portal and the Bureau of General Services',
      'MaineDOT bid opportunities',
      'cities such as Portland, Bangor, Lewiston, and Augusta',
      'towns across southern and coastal Maine',
      'the University of Maine System',
      'housing authorities, water districts, and county government',
      'regional bodies such as the Greater Portland Council of Governments',
    ],
    representativeSources: [
      'State of Maine VSS',
      'MaineDOT Bid Opportunities',
      'Maine Bureau of General Services Business Opportunities',
      'City of Portland',
      'University of Maine System — Strategic Procurement',
    ],
    opportunityCategories: [
      'general construction and municipal building projects',
      'road, bridge, and sitework',
      'plumbing and mechanical systems',
      'snow removal and seasonal services',
      'landscaping and grounds maintenance',
      'electrical and EV infrastructure',
      'professional and consulting services',
    ],
    stateValueCopy:
      'Maine’s geography makes procurement tracking genuinely hard: buyers are spread across a large, mostly rural state, and a contractor in Cumberland County may never hear about a suitable posting in Penobscot or Kennebec County until it closes. State portals, DOT lettings, university procurement, water districts, and town offices all publish separately. ProcurAlerts consolidates them and filters by the counties you actually serve, so distance stops deciding which bids you see.',
    browseUrl: `${APP_BIDS}?state=ME`,
    browseLabel: 'Browse Maine bids',
    officialOpportunities: [
      'Central purchasing for Maine’s executive branch is handled by the Office of State Procurement Services within the Department of Administrative and Financial Services. Since October 1, 2025, all State of Maine solicitations are published and managed through the state’s Vendor Self Service (VSS) portal, which offers guest access so opportunities can be viewed without a login.',
      'Construction runs through two additional official channels: MaineDOT posts highway and bridge lettings on its bid-opportunities page, and the Bureau of General Services posts state building construction work on its Business Opportunities page. Cities, towns, water districts, and the university system post separately.',
    ],
    gettingStarted: [
      'Register in the Vendor Self Service (VSS) portal with accurate NIGP commodity codes — registration is needed to respond to state solicitations, and the codes drive notification emails.',
      'Browse open solicitations in VSS (guest access works without a login), and check MaineDOT and Bureau of General Services postings for construction work.',
      'For MaineDOT lettings, review contractor prequalification early: it is generally not required for projects under $600,000 in construction cost, but MaineDOT notes that apparent low bidders who are not prequalified at bid opening face award delays and possible disqualification.',
      'Review each solicitation’s prevailing-wage, bonding, insurance, and submission requirements.',
      'Monitor the official posting for addenda and submit through the stated method.',
    ],
    registrationNotes: [
      'VSS registration creates your vendor record with the state; getting set up for payment additionally involves a Substitute W-9 filed with the Office of the State Controller. Registration is separate from prequalification — MaineDOT prequalifies contractors for its lettings, and the Bureau of General Services prequalifies general contractors project by project for major building work.',
      'Maine has no statewide general-contractor license — the Attorney General’s office notes that the state does not license home contractors. Specific trades, including electricians and plumbers, are licensed through the Office of Professional and Occupational Regulation.',
    ],
    certificationNotes: [
      'Maine does not operate a general state-level MBE, WBE, or small-business certification program for state procurement — none is offered on the official procurement pages, so treat third-party offers to sell such a credential with caution.',
      'The verified program is MaineDOT’s Disadvantaged Business Enterprise (DBE) certification, run by its Civil Rights Office under the federal USDOT program (49 CFR Part 26) for federally assisted transportation contracts; Maine also accepts interstate DBE applications.',
    ],
    prevailingWageNotes: [
      'Maine has its own prevailing-wage law (26 M.R.S. ch. 15). On public works — construction funded in whole or part by state funds with a contract amount of $50,000 or more — workers must be paid at least the fair minimum wage and benefits rates set by the Bureau of Labor Standards at the Department of Labor, which publishes rates annually.',
      'Whether a project is covered depends on its funding and contract amount; confirm the wage requirements incorporated into each solicitation. This overview is not legal advice.',
    ],
    officialResources: [
      { id: 'osps', label: 'Office of State Procurement Services', url: 'https://www.maine.gov/dafs/bbm/procurementservices' },
      { id: 'vss', label: 'Vendor Self Service (VSS) portal', url: 'https://mevss.hostams.com/PRDVSS1X1/AltSelfService' },
      { id: 'mainedot-bids', label: 'MaineDOT bid opportunities', url: 'https://www.maine.gov/dot/doing-business/bid-opportunities' },
      { id: 'bgs-opportunities', label: 'Bureau of General Services business opportunities', url: 'https://www.maine.gov/dafs/bgs/business-opportunities' },
      { id: 'mainedot-prequalification', label: 'MaineDOT contractor prequalification', url: 'https://www.maine.gov/dot/doing-business/contractor-prequalification' },
      { id: 'me-prevailing-wage', label: 'Maine prevailing wage rates (Bureau of Labor Standards)', url: 'https://www.maine.gov/labor/labor_stats/publications/wagerateconst/prevailingwage/index.shtml' },
    ],
    directAnswers: [
      {
        id: 'where-to-find',
        question: 'Where do contractors find official opportunities?',
        answer:
          'Since October 1, 2025, all State of Maine solicitations are published through the Vendor Self Service (VSS) portal, which offers guest access without a login. MaineDOT lettings and Bureau of General Services building work post on their own pages, and municipalities, water districts, and the university system post separately.',
        resourceIds: ['vss', 'mainedot-bids', 'bgs-opportunities'],
      },
      {
        id: 'vendor-registration',
        question: 'Is vendor registration required?',
        answer:
          'Guest access is enough to view state solicitations, but VSS registration — with accurate NIGP commodity codes — is needed to respond and drives the notification emails. Getting set up for payment additionally involves a Substitute W-9 filed with the Office of the State Controller.',
        resourceIds: ['vss', 'osps'],
      },
      {
        id: 'prequalification',
        question: 'Can prequalification or licensing apply?',
        answer:
          'Maine has no statewide general-contractor license. MaineDOT prequalification is generally not required for projects under $600,000 in construction cost, but apparent low bidders who are not prequalified at bid opening face award delays and possible disqualification; electricians and plumbers are licensed individually.',
        resourceIds: ['mainedot-prequalification'],
      },
      {
        id: 'certifications',
        question: 'Which certifications deserve review?',
        answer:
          'Maine operates no general state-level MBE, WBE, or small-business certification program for state procurement — treat third-party offers to sell such a credential with caution. The verified program is MaineDOT’s federal DBE certification for assisted transportation contracts.',
        resourceIds: ['osps'],
      },
      {
        id: 'prevailing-wage',
        question: 'What is the prevailing-wage rule?',
        answer:
          'Maine has its own prevailing-wage law (26 M.R.S. ch. 15): on public works funded in whole or part by state funds at $50,000 or more, workers must be paid at least the rates the Bureau of Labor Standards publishes annually.',
        resourceIds: ['me-prevailing-wage'],
      },
    ],
    taskGuides: [{ slug: 'new-england-prevailing-wage', label: 'prevailing wage across New England' }],
  },
  {
    slug: 'new-hampshire',
    name: 'New Hampshire',
    abbreviation: 'NH',
    title: 'New Hampshire Government Bids for Contractors | ProcurAlerts',
    description:
      'See New Hampshire government bids from the NH Division of Procurement, Manchester, Nashua, Concord, town offices, and the University System of New Hampshire — matched to your trade.',
    heroCopy:
      'New Hampshire public work flows through the Division of Procurement, busy municipal offices in Manchester, Nashua, and Concord, dozens of town halls, and the state’s university system. ProcurAlerts keeps daily watch over those channels and delivers the New Hampshire opportunities that match your trade and territory.',
    purchaserTypes: [
      'the New Hampshire Division of Procurement and Department of Administrative Services',
      'cities including Manchester, Nashua, and Concord',
      'towns across the state',
      'the University System of New Hampshire',
    ],
    representativeSources: [
      'NH Division of Procurement',
      'NH DAS Procurement Portal',
      'City of Manchester',
      'City of Nashua',
      'University System of New Hampshire',
    ],
    opportunityCategories: [
      'general construction and public facilities work',
      'HVAC, electrical, and plumbing upgrades',
      'paving, sitework, and sidewalk projects',
      'roofing and building envelope work',
      'janitorial and custodial services',
      'engineering and design',
      'supplies and equipment',
    ],
    stateValueCopy:
      'New Hampshire’s procurement is deliberately decentralized: beyond the state’s own purchasing, every town and school district buys independently, and postings appear on small municipal sites that are easy to overlook from outside the area. For contractors working the southern tier or the I-93 corridor, that means viable work often hides in plain sight. ProcurAlerts reads those town and city pages along with the state portals, and matches postings to your trades before deadlines close in.',
    browseUrl: `${APP_BIDS}?state=NH`,
    browseLabel: 'Browse New Hampshire bids',
    officialOpportunities: [
      'The Division of Procurement and Support Services in the Department of Administrative Services (DAS) posts statewide bids and contracts on its public Bids and Contracts portal, which can be searched by status, agency, and commodity category without an account. NHDOT advertises highway construction lettings on its own bidding pages, and the DAS Division of Public Works Design and Construction advertises state building projects.',
      'Because New Hampshire purchasing is deliberately decentralized, towns, school districts, and the university system post opportunities on their own sites alongside the state channels.',
    ],
    gettingStarted: [
      'Complete the state’s online vendor registration — DAS instructs all vendors to register, and a vendor number is issued by email, typically within one to three business days (a paper application is the published alternative).',
      'Monitor the statewide Bids and Contracts portal for opportunities in your commodity categories.',
      'For highway work, plan on NHDOT prequalification — the department requires contractors to be prequalified before bidding its projects.',
      'Review each solicitation for licensing, insurance, bonding, and submission requirements.',
      'Watch the official posting for addenda and submit exactly as the issuing agency directs.',
    ],
    registrationNotes: [
      'Vendor registration with DAS creates the vendor record the state uses to transact with your business. It is distinct from NHDOT prequalification, which applies to bidders on NHDOT highway projects, and from trade licensing: New Hampshire licenses specific trades through the Office of Professional Licensure and Certification, including electricians through the Electricians’ Board and plumbers and fuel gas fitters through the Mechanical Safety and Licensing Board.',
      'Which credential applies depends on the buyer and the work, so confirm requirements in the solicitation before bidding.',
    ],
    certificationNotes: [
      'New Hampshire is unusually straightforward here: NHDOT states explicitly that the state does not administer certification programs for women-owned (WBE), minority-owned (MBE), or minority/women-owned (MWBE) business enterprises.',
      'The one certification program available is NHDOT’s Disadvantaged Business Enterprise (DBE/ACDBE) certification under the federal USDOT program, which applies to federally assisted transportation contracts. Third-party “state certification” offers should be treated with caution.',
    ],
    prevailingWageNotes: [
      'New Hampshire’s Department of Labor does not administer a state prevailing-wage law — the wage-and-hour statutes it lists cover minimum wage, payment of wages, and related topics, not public-works rate schedules.',
      'Prevailing-wage obligations in New Hampshire generally arise under the federal Davis-Bacon Act on federally funded or assisted construction; NHDOT’s own Davis-Bacon guidance applies it to federal-aid highway contracts over $2,000. Check each solicitation for the labor standards it incorporates.',
    ],
    officialResources: [
      { id: 'nh-procurement', label: 'NH Division of Procurement and Support Services', url: 'https://www.das.nh.gov/purchasing/' },
      { id: 'nh-bids-portal', label: 'Statewide Bids and Contracts portal', url: 'https://apps.das.nh.gov/NHProcurement/Bid' },
      { id: 'nh-vendor-registration', label: 'State of NH online vendor registration', url: 'https://apps.das.nh.gov/vendorregistration/' },
      { id: 'nhdot-bids', label: 'Bid on NHDOT contracts', url: 'https://www.dot.nh.gov/doing-business-nhdot/bid-nhdot-contracts' },
      { id: 'nhdot-dbe', label: 'NHDOT DBE/ACDBE certification', url: 'https://www.dot.nh.gov/doing-business-nhdot/office-federal-compliance/disadvantaged-business-enterprise-program' },
      { id: 'nh-dol-wage-hour', label: 'NH DOL wage and hour', url: 'https://www.dol.nh.gov/inspections/wage-and-hour' },
    ],
    directAnswers: [
      {
        id: 'where-to-find',
        question: 'Where do contractors find official opportunities?',
        answer:
          'The Division of Procurement’s statewide Bids and Contracts portal can be searched by status, agency, and commodity without an account. NHDOT lettings and DAS public-works projects post separately, and towns, school districts, and the university system post on their own sites.',
        resourceIds: ['nh-bids-portal', 'nh-procurement'],
      },
      {
        id: 'vendor-registration',
        question: 'Is vendor registration required?',
        answer:
          'DAS instructs all vendors to complete the state’s online vendor registration; a vendor number is issued by email, typically within one to three business days (a paper application is the published alternative).',
        resourceIds: ['nh-vendor-registration'],
      },
      {
        id: 'prequalification',
        question: 'Can prequalification or licensing apply?',
        answer:
          'NHDOT requires contractors to be prequalified before bidding its highway projects. Specific trades are licensed through the Office of Professional Licensure and Certification, so confirm which credential a solicitation requires.',
        resourceIds: ['nhdot-bids'],
      },
      {
        id: 'certifications',
        question: 'Which certifications deserve review?',
        answer:
          'NHDOT states explicitly that New Hampshire does not administer WBE, MBE, or MWBE certification programs. The one available program is NHDOT’s federal DBE/ACDBE certification for assisted transportation contracts.',
        resourceIds: ['nhdot-dbe'],
      },
      {
        id: 'prevailing-wage',
        question: 'What is the prevailing-wage rule?',
        answer:
          'New Hampshire has no state prevailing-wage law — the Department of Labor’s wage-and-hour statutes do not include public-works rate schedules. Obligations generally arise under the federal Davis-Bacon Act; NHDOT applies it to federal-aid highway contracts over $2,000. Check each solicitation.',
        resourceIds: ['nh-dol-wage-hour', 'nhdot-dbe'],
      },
    ],
    taskGuides: [{ slug: 'new-england-prevailing-wage', label: 'prevailing wage across New England' }],
  },
  {
    slug: 'rhode-island',
    name: 'Rhode Island',
    abbreviation: 'RI',
    title: 'Rhode Island Government Bids for Contractors | ProcurAlerts',
    description:
      'Follow Rhode Island government bids from Ocean State Procures, Providence and its school district, state authorities, and cities and towns statewide — matched to your trade and service area.',
    heroCopy:
      'Rhode Island may be small, but its purchasing is anything but centralized — Ocean State Procures, Providence and its school department, quasi-public authorities, and 39 cities and towns all post separately. ProcurAlerts tracks those sources daily and brings matched Rhode Island opportunities to your inbox.',
    purchaserTypes: [
      'the RI Ocean State Procures (OSP) portal',
      'the City of Providence and Providence Public Schools',
      'cities and towns across the state',
      'Rhode Island Housing',
      'state authorities including the Narragansett Bay Commission, Quonset Development Corporation, Rhode Island Airport Corporation, and the Resource Recovery Corporation',
    ],
    representativeSources: [
      'RI Ocean State Procures (OSP)',
      'City of Providence',
      'Providence Public Schools',
      'Rhode Island Housing',
      'Narragansett Bay Commission',
    ],
    opportunityCategories: [
      'general construction and school facility projects',
      'electrical, HVAC, and fire protection work',
      'painting and finishing',
      'sitework, paving, and coastal infrastructure',
      'landscaping and grounds',
      'vehicles and fleet services',
      'IT and professional services',
    ],
    stateValueCopy:
      'Rhode Island’s compact size works in a contractor’s favor — but only if you actually see the postings. Between the state OSP portal, the Providence school district, housing and infrastructure authorities, and three dozen municipal buyers, the volume of separate channels still rivals much larger states. ProcurAlerts consolidates them into one trade-matched digest, so a school project in Providence and a facilities contract in Warwick show up side by side.',
    browseUrl: `${APP_BIDS}?state=RI`,
    browseLabel: 'Browse Rhode Island bids',
    officialOpportunities: [
      'Ocean State Procures (OSP) is Rhode Island’s eProcurement system, administered by the Division of Purchases within the Department of Administration. Its public bid board can be viewed without an account, and state agency solicitations — including RIDOT construction work — are posted there.',
      'Rhode Island’s 39 cities and towns, school departments, and quasi-public authorities continue to post their own solicitations separately, so the state portal is one channel among several.',
    ],
    gettingStarted: [
      'Create a “soft registration” in Ocean State Procures — it requires minimal information and gives immediate access to review and respond to solicitations, with commodity-code-driven notifications.',
      'Complete OSP “full registration,” including the IRS W-9 upload, before you can be awarded a state contract or receive payment.',
      'Check whether your work requires registration or licensing with the Contractors’ Registration and Licensing Board at the Department of Business Regulation.',
      'Review each solicitation’s wage, bonding, insurance, and submission requirements — and note that awarded state contracts may carry a contract administrative fee of up to 0.33% of annual contract spend under R.I. Gen. Laws § 37-2-12(b), effective January 1, 2026.',
      'Track addenda on the official posting and submit by the stated method and deadline.',
    ],
    registrationNotes: [
      'OSP registration has two tiers: the soft registration lets you review and respond to solicitations, while full registration (with a W-9) is required before award or payment. Registration is separate from contractor credentials: the Contractors’ Registration and Licensing Board registers and licenses contractors, and trade licenses such as electrical and plumbing are handled through professional regulation.',
      'RIDOT may apply its own qualification requirements to its construction lettings — confirm the current requirement in each solicitation, because RIDOT’s legacy prequalification pages no longer publish the details.',
    ],
    certificationNotes: [
      'Rhode Island’s Minority Business Enterprise Compliance Office (MBECO), part of the Division of Equity, Diversity and Inclusion in the Department of Administration, is the state’s certifying body for MBE, WBE, veteran-owned (VBE), disability-owned (BEPD), and federal DBE/ACDBE certifications, with an online application portal. The state describes certification as a marketing tool for public contracting and cautions that certification does not guarantee a contract.',
    ],
    prevailingWageNotes: [
      'Rhode Island has its own prevailing-wage law (R.I. Gen. Laws § 37-13), administered by the Department of Labor and Training. Per DLT, it applies to public works when state or municipal funds used on a project exceed $1,000, and Rhode Island generally applies the applicable federal Davis-Bacon wage determinations rather than publishing its own construction rate schedule.',
      'Covered contractors must file certified weekly payroll records; DLT also notes daily logs on general contracts of $1,000,000 or more (excluding road, highway, and bridge projects) and payment bonds on public works contracts over $50,000. Confirm the obligations attached to a specific project in the solicitation.',
    ],
    officialResources: [
      { id: 'ri-purchases', label: 'RI Division of Purchases', url: 'https://ridop.ri.gov/' },
      { id: 'osp-overview', label: 'Ocean State Procures (OSP) overview and registration', url: 'https://ridop.ri.gov/ocean-state-procures-osp' },
      { id: 'osp-faqs', label: 'OSP frequently asked questions', url: 'https://ridop.ri.gov/ocean-state-procures-osp/faqs' },
      { id: 'ri-prevailing-wage', label: 'RI DLT prevailing wage', url: 'https://dlt.ri.gov/regulation-and-safety/prevailing-wage' },
      { id: 'mbeco', label: 'MBE Compliance Office (DEDI)', url: 'https://dedi.ri.gov/division-units/minority-business-enterprise-compliance-office' },
      { id: 'crlb', label: 'Contractors’ Registration and Licensing Board (DBR)', url: 'https://dbr.ri.gov/contractors-and-building-design-fire-professionals-board-accountancy/contractors-registration-and' },
      { id: 'contract-admin-fee', label: 'Contract administrative fee FAQ (effective January 1, 2026)', url: 'https://ridop.ri.gov/vendors/faq-contract-administrative-fee-assessment-effective-january-1-2026' },
    ],
    directAnswers: [
      {
        id: 'where-to-find',
        question: 'Where do contractors find official opportunities?',
        answer:
          'State agency solicitations — including RIDOT construction work — are posted on Ocean State Procures (OSP), whose public bid board can be viewed without an account. The 39 cities and towns, school departments, and quasi-public authorities post separately.',
        resourceIds: ['osp-overview', 'ri-purchases'],
      },
      {
        id: 'vendor-registration',
        question: 'Is vendor registration required?',
        answer:
          'OSP’s “soft registration” takes minimal information and gives immediate access to review and respond to solicitations. Full registration, including the IRS W-9 upload, is required before a state contract can be awarded or paid.',
        resourceIds: ['osp-overview', 'osp-faqs'],
      },
      {
        id: 'prequalification',
        question: 'Can prequalification or licensing apply?',
        answer:
          'Contractor registration and licensing run through the Contractors’ Registration and Licensing Board at the Department of Business Regulation. RIDOT may apply its own qualification requirements — confirm the current requirement in each solicitation.',
        resourceIds: ['crlb'],
      },
      {
        id: 'certifications',
        question: 'Which certifications deserve review?',
        answer:
          'The Minority Business Enterprise Compliance Office (MBECO) certifies MBE, WBE, veteran-owned, disability-owned, and federal DBE/ACDBE firms through an online portal. The state describes certification as a marketing tool and cautions that it does not guarantee a contract.',
        resourceIds: ['mbeco'],
      },
      {
        id: 'prevailing-wage',
        question: 'What is the prevailing-wage rule?',
        answer:
          'Rhode Island has its own prevailing-wage law (R.I. Gen. Laws § 37-13): per DLT it applies to public works when state or municipal funds used on a project exceed $1,000, generally applying federal Davis-Bacon wage determinations, with certified weekly payroll filings.',
        resourceIds: ['ri-prevailing-wage'],
      },
      {
        id: 'contract-fee',
        question: 'Is there a state contract administrative fee?',
        answer:
          'Awarded state contracts may carry a contract administrative fee of up to 0.33% of annual contract spend under R.I. Gen. Laws § 37-2-12(b), effective January 1, 2026 — check the assessment FAQ when pricing state work.',
        resourceIds: ['contract-admin-fee'],
      },
    ],
    taskGuides: [{ slug: 'new-england-prevailing-wage', label: 'prevailing wage across New England' }],
  },
  {
    slug: 'vermont',
    name: 'Vermont',
    abbreviation: 'VT',
    title: 'Vermont Government Bids for Contractors | ProcurAlerts',
    description:
      'Find Vermont government bids from the state’s EBB registry, Burlington and other municipalities, the Vermont State Colleges System, and town offices — matched to your trade and territory.',
    heroCopy:
      'Vermont posts public work through its EBB business registry, Burlington and other municipal offices, the state colleges, and town governments from Colchester to Bennington. ProcurAlerts monitors those sources every day and delivers the Vermont opportunities that fit your trade and service area.',
    purchaserTypes: [
      'the Vermont Business Registry — EBB state posting system',
      'the City of Burlington and other cities',
      'towns across the state',
      'the Vermont State Colleges System',
    ],
    representativeSources: [
      'Vermont Business Registry — EBB',
      'City of Burlington',
      'Vermont State Colleges System',
      'City of Montpelier',
      'Town of Bennington',
    ],
    opportunityCategories: [
      'general construction and municipal projects',
      'HVAC and energy-efficiency upgrades',
      'electrical and EV charging infrastructure',
      'sitework and paving',
      'snow removal and winter services',
      'environmental and facilities services',
      'engineering and professional services',
    ],
    stateValueCopy:
      'Vermont’s procurement reflects the state itself: a handful of larger buyers in Burlington and Montpelier, a state posting registry, the college system, and many small towns whose postings are easy to miss if you are not local. For contractors who cover multiple counties, the challenge is not competition — it is visibility. ProcurAlerts reads those sources daily and filters by the trades and territory you choose, so a town project in southern Vermont surfaces alongside state-level work.',
    browseUrl: `${APP_BIDS}?state=VT`,
    browseLabel: 'Browse Vermont bids',
    officialOpportunities: [
      'Vermont’s current eProcurement system is VTBuys, where suppliers register, view bid opportunities, and submit bids; older materials that reference “VTBid” describe a retired system. The Office of Purchasing and Contracting in the Department of Buildings and General Services (BGS) also maintains openly browsable bid information, construction plan-holder lists, and bid tabulations, and VTrans advertises transportation lettings separately.',
      'By statute (29 V.S.A. § 161(a)), state construction projects over $50,000 must be publicly advertised or awarded by inviting three or more bids. Municipalities, the state colleges, and the Vermont Business Registry post additional local opportunities outside the state portal.',
    ],
    gettingStarted: [
      'Register as a supplier in VTBuys — the state describes it as a no-cost, self-service platform, and registration enables bid notifications and electronic submission.',
      'Check the BGS Bid Information page, which is openly browsable without an account, for current state postings and bid tabulations.',
      'Residential contractors: confirm registration with the Secretary of State’s Office of Professional Regulation, required for residential contracting work of $10,000 or more. Trade licenses such as electrical and plumbing run through the Department of Public Safety’s Division of Fire Safety.',
      'For transportation work, confirm VTrans prequalification before bidding its construction projects.',
      'Review wage, bonding, insurance, and submission requirements in each solicitation, track addenda, and submit as instructed.',
    ],
    registrationNotes: [
      'Three registrations are easy to confuse in Vermont: VTBuys supplier registration (to participate in state purchasing), Secretary of State business registration (to form or register the business itself), and OPR residential-contractor registration (required for residential contracting work valued at $10,000 or more, with fees of $75 for individuals and $250 for businesses). None substitutes for the others.',
      'VTrans runs its own prequalification program for bidders on its construction projects. Vermont does not issue a general commercial contractor license; trade licensing is handled by the Division of Fire Safety.',
    ],
    certificationNotes: [
      'Vermont’s official procurement pages do not list a general statewide small-business or minority/women-owned certification program. BGS maintains a minority- and women-owned business participation policy (under Executive Order 15-91) and publishes an M/WBE directory, but this is a policy and directory rather than a certification.',
      'The exception is federal: the VTrans Office of Civil Rights administers Disadvantaged Business Enterprise (DBE) certification for USDOT-assisted transportation contracts.',
    ],
    prevailingWageNotes: [
      'Vermont has a state construction prevailing-wage requirement, but a narrow one: under 29 V.S.A. § 161(b), state building construction projects with a construction cost over $100,000 must pay at least the mean prevailing wage published by the Vermont Department of Labor plus a fringe benefit of 42.5% of wage, with parallel coverage for projects over $200,000 that are at least half funded through specified capital funds.',
      'The statute exempts Agency of Transportation and Forests, Parks and Recreation projects and does not extend to purely municipal work; federally funded highway projects follow federal Davis-Bacon rules instead. Confirm which labor standards a solicitation incorporates.',
    ],
    officialResources: [
      { id: 'bgs-purchasing', label: 'Office of Purchasing and Contracting (BGS)', url: 'https://bgs.vermont.gov/purchasing-contracting' },
      { id: 'vtbuys-portal', label: 'VTBuys supplier portal', url: 'https://vtbuys.suppliers.vermont.gov/page.aspx/en/usr/login' },
      { id: 'vtbuys-guidance', label: 'VTBuys guidance for suppliers and bidders', url: 'https://vtbuysprocurement.vermont.gov/suppliers-bidders' },
      { id: 'bgs-bids', label: 'BGS bid information (open browse)', url: 'https://bgs.vermont.gov/purchasing/bids' },
      { id: 'vt-construction-statute', label: 'State construction statute, 29 V.S.A. § 161', url: 'https://legislature.vermont.gov/statutes/section/29/005/00161' },
      { id: 'vtrans-bids', label: 'VTrans bid opportunities and prequalification', url: 'https://vtrans.vermont.gov/contract-admin/bids-requests' },
    ],
    directAnswers: [
      {
        id: 'where-to-find',
        question: 'Where do contractors find official opportunities?',
        answer:
          'State bids run through VTBuys, where suppliers register, view opportunities, and submit bids; BGS bid information and tabulations are openly browsable without an account, and VTrans advertises transportation lettings separately. By statute (29 V.S.A. § 161(a)), state construction projects over $50,000 must be publicly advertised or awarded by inviting three or more bids.',
        resourceIds: ['vtbuys-portal', 'bgs-bids', 'vt-construction-statute'],
      },
      {
        id: 'vendor-registration',
        question: 'Is vendor registration required?',
        answer:
          'VTBuys supplier registration is no-cost and self-service, and enables bid notifications and electronic submission; browsing BGS bid information needs no account. Note that VTBuys registration, Secretary of State business registration, and contractor credentials are separate steps.',
        resourceIds: ['vtbuys-guidance'],
      },
      {
        id: 'prequalification',
        question: 'Can prequalification or licensing apply?',
        answer:
          'Vermont issues no general commercial contractor license, but residential contracting work of $10,000 or more requires registration with the Secretary of State’s Office of Professional Regulation, and VTrans prequalifies bidders for its construction projects.',
        resourceIds: ['vtrans-bids'],
      },
      {
        id: 'certifications',
        question: 'Which certifications deserve review?',
        answer:
          'Vermont’s official procurement pages list no general statewide small-business or minority/women-owned certification program — BGS maintains an M/WBE participation policy and directory rather than a certification. The exception is VTrans’s federal DBE certification for USDOT-assisted transportation contracts.',
        resourceIds: ['bgs-purchasing', 'vtrans-bids'],
      },
      {
        id: 'prevailing-wage',
        question: 'What is the prevailing-wage rule?',
        answer:
          'Vermont’s state rule is narrow: state building construction over $100,000 must pay at least the Department of Labor’s mean prevailing wage plus a 42.5% fringe benefit (29 V.S.A. § 161(b)), with parallel coverage for projects over $200,000 that are at least half funded through specified capital funds. VTrans and municipal work are exempt, and federal-aid highway projects follow Davis-Bacon instead.',
        resourceIds: ['vt-construction-statute'],
      },
    ],
    taskGuides: [{ slug: 'new-england-prevailing-wage', label: 'prevailing wage across New England' }],
  },
]

export const STATE_ROUTES = STATES.map((s) => `/government-bids/${s.slug}`)

export function stateBySlug(slug: string): StateContent | undefined {
  return STATES.find((s) => s.slug === slug)
}

export function stateBreadcrumbJsonLd(state: StateContent): string {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Coverage', item: `${SITE_URL}/coverage` },
      {
        '@type': 'ListItem',
        position: 3,
        name: state.name,
        item: `${SITE_URL}/government-bids/${state.slug}`,
      },
    ],
  })
}
